import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ModalContainer } from '../../../common';
import { DirectDepositContent } from './DirectDepositContent';
import { CheckContent } from './CheckContent';
import { ReimbursementMethodOption } from './ReimbursementMethodOption';
import { ActionButton } from '../../../common/petCloud';
import { CareCreditContent } from './CareCreditContent';

import closeIcon from '../../../../assets/close-button.svg';
import { REIMBURSEMENT_METHODS } from '../../../../constants';

import {
  changeReimbursementMethodType,
  verifyCodeReset,
} from '../../../../actions';
import { useCareCreditReimbursement } from '../../../../hooks/useFeatureFlags';
import {
  useCareCreditValidation,
  useReimbursementMethodDisclaimer,
} from '../../../../hooks';
import { useUpdateReimbursementMethodEvent } from './hooks';

import '../css/ReimbursementEditModal.css';
import styles from '../css/ReimbursementEditModal.module.css';

const STEPS = {
  reimbursementDetail: 2,
  selectMethod: 1,
};

// display order differs between designs: the redesign leads with the
// recommended (direct deposit) option, the legacy list keeps check first
const LEGACY_METHODS_ORDER = [
  REIMBURSEMENT_METHODS.check,
  REIMBURSEMENT_METHODS.deposit,
  REIMBURSEMENT_METHODS.careCredit,
];

const NEW_DESIGN_METHODS_ORDER = [
  REIMBURSEMENT_METHODS.deposit,
  REIMBURSEMENT_METHODS.check,
  REIMBURSEMENT_METHODS.careCredit,
];

const ReimbursementMethodList = ({ preferredMethod, onNextClick }) => {
  const { t } = useTranslation('myAccount');
  const careCreditFlag = useCareCreditReimbursement();
  const { careCredit } = useCareCreditValidation();
  const newDesign = useReimbursementMethodDisclaimer();
  const dispatch = useDispatch();

  const getClassName = useCallback((isActive) => `${styles.radioButton} `
    + `${isActive ? styles.radioButtonSelected : ''}`, []);

  const onRadioButtonChange = useCallback((e) => {
    const { value } = e.target;
    dispatch(changeReimbursementMethodType(Number(value)));
  }, []);

  const showCareCredit = (careCreditFlag && careCredit.hasCareCreditUrl);

  const visibleMethods = useMemo(() => {
    const methodsOrder = newDesign
      ? NEW_DESIGN_METHODS_ORDER : LEGACY_METHODS_ORDER;

    return methodsOrder.filter((method) => (
      method !== REIMBURSEMENT_METHODS.careCredit || showCareCredit
    ));
  }, [newDesign, showCareCredit]);

  return (
    <div className={styles.reimbursementMethodContainer}>
      <fieldset className={newDesign ? styles.fieldsetNewDesign : ''}>
        <legend className={styles.legend}>
          {t('reimbursementSection.editModal.selectMethod')}
        </legend>

        {visibleMethods.map((method) => {
          const isActive = preferredMethod === method;

          return (
            <ReimbursementMethodOption
              key={method}
              checked={isActive}
              legacyClassName={getClassName(isActive)}
              method={method}
              newDesign={newDesign}
              onChange={onRadioButtonChange}
            />
          );
        })}
      </fieldset>

      <div className={styles.actions}>
        <ActionButton
          onClick={onNextClick}
          title={t('reimbursementSection.editModal.next')}
        />
      </div>
    </div>
  );
};

const ReimbursementMethodDetail = ({
  cancelAll = () => { },
  method,
  onCancel,
}) => {
  switch (method) {
    case REIMBURSEMENT_METHODS.deposit:
      return <DirectDepositContent onCancel={onCancel} />;
    case REIMBURSEMENT_METHODS.check:
      return <CheckContent onCancel={onCancel} />;
    case REIMBURSEMENT_METHODS.careCredit:
      return <CareCreditContent cancelAll={cancelAll} onCancel={onCancel} />;
    default:
      return null;
  }
};

const ReimbursementEditModal = ({
  isEditing = false,
  isVisible = false,
  onCancel = () => { },
  policyNumber,
  source,
}) => {
  const { t } = useTranslation('myAccount');
  const dispatch = useDispatch();
  const store = useSelector(({ billingPayments }) => ({
    billingPayments,
  }), shallowEqual);
  const newDesign = useReimbursementMethodDisclaimer();

  useUpdateReimbursementMethodEvent({ policyNumber, source });

  const [step, setStep] = useState(STEPS.selectMethod);
  const {
    billingPayments: {
      reimbursementData: { PreferredReimbursementMethod },
    },
  } = store;

  const goToStep = useCallback((newStep) => () => {
    setStep(newStep);
  }, []);

  useEffect(() => {
    if (isVisible && isEditing) {
      setStep(STEPS.reimbursementDetail);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) {
      setStep(STEPS.selectMethod);

      // reset verification token after use
      dispatch(verifyCodeReset());
    }
  }, [isVisible]);

  const stepRendered = useMemo(() => {
    switch (step) {
      case STEPS.reimbursementDetail:
        return (
          <ReimbursementMethodDetail
            cancelAll={onCancel}
            method={PreferredReimbursementMethod}
            onCancel={goToStep(STEPS.selectMethod)}
          />
        );
      case STEPS.selectMethod:
      default:
        return (
          <ReimbursementMethodList
            onNextClick={goToStep(STEPS.reimbursementDetail)}
            preferredMethod={PreferredReimbursementMethod}
          />
        );
    }
  }, [step, PreferredReimbursementMethod, goToStep]);

  if (!isVisible) {
    return null;
  }

  return (
    <ModalContainer show>
      <div className={`${styles.container} ${
        newDesign && step === STEPS.selectMethod ? styles.width700 : ''}`}
      >
        <div className={styles.titleContainer}>
          <h3>{t('reimbursementSection.editModal.title')}</h3>

          <button
            className={styles.closeIconContainer}
            id="closeIconEditReimbursementModal"
            onClick={onCancel}
            type="button"
          >
            <img
              alt={t('reimbursementSection.editModal.close')}
              src={closeIcon}
            />
          </button>
        </div>

        {stepRendered}
      </div>
    </ModalContainer>
  );
};

export { ReimbursementEditModal };
